import {
  SKY_LEVIATHAN_JOURNEY,
  skyLeviathanJourneyView,
} from '../../../packages/domain/src/105-sky-leviathan-journey.mjs';
import { createSkySeaMaterial } from './174-sky-ground-material.mjs';
import { SKY_DECORATIVE_QUALITY } from './172-sky-adaptive-quality.mjs';
import {
  SKY_INDUSTRIAL_SCENERY as SCALE,
  SKY_WORLD,
} from '../../../packages/domain/src/86-sky-world-scale.mjs';
import { SKY_TERRAIN, skyTerrainMeshHeight } from '../../../packages/domain/src/87-sky-terrain.mjs';

/** Original distant refinery dressing. Every visible structure is grounded
 * in metres outside the playable whale orbit. No decorative mesh owns damage,
 * collision or a gameplay target. The caller owns the scene attachment. */
export function createSkyIndustrialWorld(T) {
  const group = new T.Group(),
    resources = new Set(),
    towers = [];
  group.name = 'sky-gilded-industrial-landscape';
  group.visible = false;
  const own = (resource) => {
    resources.add(resource);
    return resource;
  };
  const batches = [];
  let towerReady = false;
  let lastTravel = null;
  let lastCityOffset = null;
  let lastApproach = null;
  let eligibleCount = 0;
  let sourceBounds = null;
  const projection = new T.Matrix4(),
    instanceMatrix = new T.Matrix4(),
    frustum = new T.Frustum(),
    visibleBounds = new T.Box3(),
    corner = new T.Vector3();
  const seaMaterial = own(createSkySeaMaterial(T));
  const sea = new T.Mesh(own(new T.PlaneGeometry(1400, 1800)), seaMaterial);
  sea.name = 'sky-industrial-sea';
  sea.rotation.x = -Math.PI / 2;
  sea.position.set(0, SKY_LEVIATHAN_JOURNEY.seaY, -400);
  group.add(sea);
  // Include each terrain-cell diagonal crossing, so the cliff and clipped land
  // share the exact triangle edge rather than a second approximate heightfield.
  const stations = SKY_TERRAIN.groundColumns * 2 + 1;
  const cliffGeometry = own(new T.BufferGeometry());
  const cliffPositions = new T.BufferAttribute(new Float32Array(stations * 6), 3);
  cliffPositions.setUsage(T.DynamicDrawUsage);
  cliffGeometry.setAttribute('position', cliffPositions);
  const normals = new Float32Array(stations * 6),
    indices = [];
  for (let i = 0; i < stations; i++) {
    normals[i * 6 + 2] = normals[i * 6 + 5] = 1;
    if (i < stations - 1) {
      const a = i * 2;
      indices.push(a, a + 1, a + 2, a + 2, a + 1, a + 3);
    }
  }
  cliffGeometry.setAttribute('normal', new T.BufferAttribute(normals, 3));
  cliffGeometry.setIndex(indices);
  const cliffMaterial = own(
    new T.MeshStandardMaterial({
      color: '#79644c',
      roughness: 1,
      transparent: true,
      side: T.DoubleSide,
    }),
  );
  const cliff = new T.Mesh(cliffGeometry, cliffMaterial);
  cliff.name = 'sky-industrial-coastal-cliff';
  cliff.frustumCulled = false;
  group.add(cliff);
  const terrainSample = { normal: [0, 1, 0] };
  const clouds = new T.Group();
  clouds.name = 'sky-gilded-cloud-banks';
  group.add(clouds);
  return {
    group,
    setTowerModel(model) {
      if (towerReady) return;
      model.updateMatrixWorld(true);
      const bounds = new T.Box3().setFromObject(model),
        height = bounds.max.y - bounds.min.y,
        center = bounds.getCenter(new T.Vector3());
      if (!Number.isFinite(height) || Math.abs(height - SCALE.modelHeight) > 0.01)
        throw new Error('Refinery model differs from its measured world scale');
      sourceBounds = bounds;
      SCALE.positions.forEach((position, index) => {
        const tower = new T.Group(),
          scale = (position.height ?? SCALE.heights[index % SCALE.heights.length]) / height;
        tower.position.set(position.x, 0, position.z);
        tower.userData.cityZ = position.z;
        tower.userData.distant = position.distant;
        tower.scale.setScalar(scale);
        tower.userData.offset = new T.Matrix4().makeTranslation(
          -center.x,
          -bounds.min.y,
          -center.z,
        );
        towers.push(tower);
      });
      model.traverse((node) => {
        if (!node.isMesh) return;
        // Model cache owns shared geometry/material/maps; this world owns only
        // the instance buffers. Disposal cannot invalidate a prepared next view.
        const mesh = own(new T.InstancedMesh(node.geometry, node.material, towers.length));
        mesh.name = 'industrial-structure-batch';
        mesh.receiveShadow = true;
        batches.push({ mesh, matrix: node.matrixWorld.clone() });
        group.add(mesh);
      });
      towerReady = true;
      lastTravel = null;
    },
    setTowerLodModel(model) {
      if (!towerReady) throw new Error('Refinery LOD requires its original material model first');
      if (batches.some((batch) => batch.lod)) return;
      const meshes = [];
      model.updateWorldMatrix(true, true);
      model.traverse((node) => {
        if (node.isMesh) meshes.push(node);
      });
      const bounds = new T.Box3().setFromObject(model);
      if (
        meshes.length !== 1 ||
        batches.length !== 1 ||
        Math.abs(bounds.max.y - bounds.min.y - SCALE.modelHeight) > 0.01
      )
        throw new Error('Refinery LOD differs from its original single-material44m contract');
      sourceBounds.union(bounds);
      const original = batches[0],
        node = meshes[0],
        mesh = own(new T.InstancedMesh(node.geometry, original.mesh.material, towers.length));
      mesh.name = 'industrial-structure-lod-batch';
      mesh.receiveShadow = original.mesh.receiveShadow;
      mesh.count = 0;
      original.lod = { mesh, matrix: node.matrixWorld.clone() };
      group.add(mesh);
    },
    setCloudTexture(texture) {
      if (clouds.children.length) return;
      const material = own(
        new T.SpriteMaterial({
          map: texture,
          transparent: true,
          opacity: 0.72,
          depthWrite: false,
          depthTest: true,
          fog: false,
          toneMapped: true,
        }),
      );
      // The deck lies below every active whale surface. Central banks remain
      // beyond incoming formation lanes while the pilot crosses its altitude.
      for (let i = 0; i < 12; i++) {
        const cloud = new T.Sprite(material);
        cloud.position.set(
          ((i % 3) - 1) * 110,
          SKY_LEVIATHAN_JOURNEY.cloudCenterY + (i % 3),
          (i % 3 === 1 ? -170 : 8) - Math.floor(i / 3) * 65,
        );
        cloud.scale.set(130, SKY_LEVIATHAN_JOURNEY.cloudHeight, 1);
        cloud.userData.basePosition = cloud.position.clone();
        clouds.add(cloud);
      }
      const deckMaterial = own(material.clone());
      for (const x of [-70, 0, 70]) {
        const cloud = new T.Sprite(deckMaterial);
        cloud.position.set(x, SKY_LEVIATHAN_JOURNEY.cloudCenterY, -40);
        cloud.scale.set(150, SKY_LEVIATHAN_JOURNEY.cloudHeight, 1);
        cloud.userData.encounterDeck = true;
        cloud.userData.basePosition = cloud.position.clone();
        clouds.add(cloud);
      }
    },
    draw(state, active, decoration = SKY_DECORATIVE_QUALITY[0], quiet = false) {
      group.visible = active;
      if (!active) return;
      const arc = skyLeviathanJourneyView(state);
      cliff.visible = arc.landVisibility > 0;
      cliffMaterial.opacity = arc.landVisibility;
      eligibleCount = Math.min(
        towers.length,
        Math.max(10, Math.ceil(towers.length * decoration.scenery)),
      );
      for (const batch of batches) {
        batch.mesh.count = eligibleCount;
        if (batch.lod) batch.lod.mesh.count = 0;
      }
      const travel = Number.isFinite(state.terrainTravel) ? Math.max(0, state.terrainTravel) : 0;
      const approach = quiet ? 0 : Math.exp(-travel / 500) * (1 - arc.progress) ** 2;
      seaMaterial.uniforms.time.value = quiet ? 0 : (state.time ?? 0);
      seaMaterial.uniforms.travel.value = quiet ? 0 : travel;
      seaMaterial.uniforms.shorelineZ.value = arc.shorelineZ;
      const atmosphereTime = Number.isFinite(state.time) ? state.time : 0;
      clouds.children.forEach((cloud, index) => {
        cloud.visible = index % decoration.cloudStride === 0;
        cloud.position.copy(cloud.userData.basePosition);
        // Finite approach keeps source banks ahead of the flight lane. There
        // is no recycle pop, and the accepted encounter deck stays in place.
        if (!quiet && !cloud.userData.encounterDeck)
          cloud.position.z -=
            240 *
            Math.exp(-travel / 650) *
            (1 - arc.progress) ** 2 *
            (Math.abs(cloud.userData.basePosition.x) < 40 ? 0.22 : 1);
        // Bounded, slow bank drift adds depth without crossing the active
        // hull or creating extra transparent layers. Paused time freezes it.
        if (!quiet) {
          cloud.position.x += Math.sin(atmosphereTime * 0.055 + index * 1.7) * 3;
          cloud.position.y += (Math.sin(atmosphereTime * 0.075 + index) - 1) * 0.225;
          const drift = Math.cos(atmosphereTime * 0.035 + index * 0.8);
          cloud.position.z +=
            Math.abs(cloud.userData.basePosition.x) < 40 && !cloud.userData.encounterDeck
              ? drift - 1
              : drift * 2;
        }
        cloud.material.rotation = quiet ? 0 : Math.sin(atmosphereTime * 0.045) * 0.012;
        if (cloud.userData.encounterDeck)
          cloud.material.opacity = arc.cloudOpacity * arc.deckOpacity;
      });
      if (clouds.children.length)
        clouds.children[0].material.opacity =
          arc.cloudOpacity * (1 - (state.leviathan?.systems?.crashDescent ?? 0) * 0.85);
      if (lastTravel === travel && lastCityOffset === arc.cityOffsetZ && lastApproach === approach)
        return;
      lastApproach = approach;
      lastTravel = travel;
      lastCityOffset = arc.cityOffsetZ;
      const row =
        ((arc.shorelineZ - SKY_TERRAIN.groundCenterZ + SKY_TERRAIN.groundLength / 2) *
          SKY_TERRAIN.groundRows) /
        SKY_TERRAIN.groundLength;
      const diagonal = 1 - (row - Math.floor(row));
      for (let i = 0; i < stations; i++) {
        const column = Math.floor(i / 2) + (i % 2 ? diagonal : 0);
        const x =
          (column * SKY_TERRAIN.groundWidth) / SKY_TERRAIN.groundColumns -
          SKY_TERRAIN.groundWidth / 2;
        cliffPositions.setXYZ(
          i * 2,
          x,
          skyTerrainMeshHeight(x, arc.shorelineZ, travel, terrainSample) + SKY_WORLD.terrainY,
          arc.shorelineZ,
        );
        cliffPositions.setXYZ(i * 2 + 1, x, SKY_LEVIATHAN_JOURNEY.seaY - 3, arc.shorelineZ);
      }
      cliffPositions.needsUpdate = true;
      for (const tower of towers) {
        // Near structures close faster than the far skyline while always
        // remaining inland. At cruise the canonical crash layout is exact.
        tower.position.z =
          tower.userData.cityZ +
          (tower.userData.distant ? 0 : arc.cityOffsetZ) -
          80 * approach * (tower.userData.distant ? 0.22 : 1);
        tower.position.y =
          skyTerrainMeshHeight(tower.position.x, tower.position.z, travel) + SKY_WORLD.terrainY;
        tower.updateMatrixWorld(true);
      }
      for (const batch of batches) {
        towers.forEach((tower, index) => {
          instanceMatrix
            .copy(tower.matrixWorld)
            .multiply(tower.userData.offset)
            .multiply(batch.matrix);
          batch.mesh.setMatrixAt(index, instanceMatrix);
        });
        batch.mesh.instanceMatrix.needsUpdate = true;
        batch.mesh.computeBoundingSphere();
      }
    },
    /** Compact decorative instances against the final camera using the full
     * original model bounds. An InstancedMesh's collective bound otherwise
     * submits every18K-triangle tower whenever any part of the city is visible.
     * Recompute from source transforms, so a paused camera turn restores every
     * newly visible tower without relying on simulation time or stale indices. */
    cull(camera, viewportHeight = globalThis.innerHeight || 844) {
      if (!group.visible || !sourceBounds) return 0;
      camera.updateMatrixWorld(true);
      group.updateWorldMatrix(true, false);
      frustum.setFromProjectionMatrix(
        projection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse),
        camera.coordinateSystem,
      );
      const visible = [],
        distant = [];
      for (let index = 0; index < eligibleCount; index++) {
        const tower = towers[index];
        instanceMatrix
          .copy(group.matrixWorld)
          .multiply(tower.matrixWorld)
          .multiply(tower.userData.offset);
        visibleBounds.copy(sourceBounds).applyMatrix4(instanceMatrix);
        if (!frustum.intersectsBox(visibleBounds)) continue;
        let low = Infinity,
          high = -Infinity,
          near = false;
        for (const x of [visibleBounds.min.x, visibleBounds.max.x])
          for (const y of [visibleBounds.min.y, visibleBounds.max.y])
            for (const z of [visibleBounds.min.z, visibleBounds.max.z]) {
              corner.set(x, y, z).applyMatrix4(camera.matrixWorldInverse);
              if (corner.z >= -camera.near) near = true;
              corner.applyMatrix4(camera.projectionMatrix);
              low = Math.min(low, corner.y);
              high = Math.max(high, corner.y);
            }
        const pixels = near ? Infinity : ((high - low) * Math.max(1, viewportHeight)) / 2;
        // Keep close galleries sharp; hysteresis prevents topology flicker
        // while the camera or grounded city moves around the LOD threshold.
        if (pixels < 140) tower.userData.lod = true;
        else if (pixels > 170) tower.userData.lod = false;
        (tower.userData.lod && batches[0]?.lod ? distant : visible).push(index);
      }
      for (const batch of batches) {
        for (const [target, indices] of [
          [batch, visible],
          [batch.lod, distant],
        ]) {
          if (!target) continue;
          indices.forEach((index, slot) => {
            const tower = towers[index];
            instanceMatrix
              .copy(tower.matrixWorld)
              .multiply(tower.userData.offset)
              .multiply(target.matrix);
            target.mesh.setMatrixAt(slot, instanceMatrix);
          });
          target.mesh.count = indices.length;
          target.mesh.instanceMatrix.needsUpdate = true;
          target.mesh.computeBoundingSphere();
        }
      }
      return visible.length + distant.length;
    },
    dispose() {
      group.removeFromParent();
      for (const resource of resources) resource.dispose();
      resources.clear();
    },
  };
}
